import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Plus, Edit, Trash2 } from 'lucide-react';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Pagination from '../../components/Common/Pagination';
import { getEquipment, createEquipment, updateEquipment, deleteEquipment, getCurrentUser, getUserRole } from '../../services/supabase';
import { useNavigate } from 'react-router-dom';
import { DefaultButton, PrimaryButton } from '../../components/Common/BaseButton';
import { useMediaQuery } from 'react-responsive';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 2rem;
  margin: 1rem 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: start;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0 0;
  color: #888;
`;

const SearchBar = styled.input`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  flex: 1;
  max-width: 300px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &.danger:hover {
    background-color: #fee;
    border-color: #fcc;
    color: #c44;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: none;
  border: 1px solid #ececec;
`;

const TableHeader = styled.th`
  background: none;
  color: #333;
  padding: 1rem 1rem 0.5rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 1.05rem;
  border-bottom: 2px solid #ececec;
`;

const TableRow = styled.tr`
  background: white;
  &:not(:last-child) {
    border-bottom: 1px solid #ececec;
  }
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 1.1rem 1rem 1.1rem 1rem;
  border-bottom: 1px solid #ececec;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #000;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const TopControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

const MobileCard = styled.div`
  background: white;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MobileCardTitle = styled.h4`
  margin: 0;
  font-weight: 600;
  color: #333;
  max-width: 65%;
  overflow-wrap: anywhere;
`;

const MobileActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EquipmentPage = () => {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [roleChecked, setRoleChecked] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);

  const isMobile = useMediaQuery({ maxWidth: 699 });

  // Scroll prevention for modals
  useEffect(() => {
    if (showModal || showDeleteConfirm) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showDeleteConfirm]);

  // Check manager role
  useEffect(() => {
    async function checkRole() {
      const { user } = await getCurrentUser();
      if (user) {
        setCurrentUserId(user.id);
        const { role } = await getUserRole(user.id);
        if (role !== 'manager') {
          navigate('/dashboard', { replace: true });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
      setRoleChecked(true);
    }
    checkRole();
  }, [navigate]);

  // Fetch equipment
  useEffect(() => {
    if (!roleChecked) return;
    
    const fetchEquipment = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await getEquipment();
        if (error) throw error;
        setEquipment(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load equipment');
      }
      setLoading(false);
    };

    fetchEquipment();
  }, [roleChecked]);

  // Filter and paginate equipment
  const filteredEquipment = equipment.filter(eq =>
    eq.name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredEquipment.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEquipment = filteredEquipment.slice(startIndex, endIndex);

  const handleAddEquipment = () => {
    setModalMode('add');
    setCurrentEquipment(null);
    setFormData({ name: '' });
    setShowModal(true);
  };

  const handleEditEquipment = (eq) => {
    setModalMode('edit');
    setCurrentEquipment(eq);
    setFormData({ name: eq.name });
    setShowModal(true);
  };

  const handleDeleteEquipment = (eq) => {
    setEquipmentToDelete(eq);
    setShowDeleteConfirm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSubmitting(true);
    try {
      if (modalMode === 'add') {
        const { data, error } = await createEquipment(formData.name.trim());
        if (error) throw error;
        setEquipment(prev => [...prev, data[0]]);
      } else {
        const { data, error } = await updateEquipment(currentEquipment.id, { name: formData.name.trim() });
        if (error) throw error;
        setEquipment(prev => prev.map(eq => eq.id === currentEquipment.id ? data[0] : eq));
      }
        // Sort equipment by name
        setEquipment(prev => prev.sort((a, b) => a.name.localeCompare(b.name)));
      setShowModal(false);
    } catch (err) {
      setError(err.message || 'Failed to save equipment');
    }
    setSubmitting(false);
  };

  const confirmDelete = async () => {
    if (!equipmentToDelete) return;
    
    try {
      const { error } = await deleteEquipment(equipmentToDelete.id);
      if (error) throw error;
      setEquipment(prev => prev.filter(eq => eq.id !== equipmentToDelete.id));
      setShowDeleteConfirm(false);
      setEquipmentToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete equipment');
    }
  };

  if (!roleChecked || loading) {
    return (
      <PageContainer>
        <LoadingSpinner text="Loading Equipment..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Card>
          <Title>Error</Title>
          <p>{error}</p>
          <DefaultButton onClick={() => window.location.reload()}>
            Retry
          </DefaultButton>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <HeaderRow>
          <div>
            <Title>Equipment Management</Title>
          </div>
          <TopControls>
            <SearchBar
              placeholder="Search equipment..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <PrimaryButton onClick={handleAddEquipment}>
              <Plus size={18} />
              Add Equipment
            </PrimaryButton>
          </TopControls>
        </HeaderRow>

        {isMobile ? (
          // Mobile view
          <div>
            {paginatedEquipment.length === 0 ? (
              <p style={{ color: '#888', fontStyle: 'italic' }}>
                {search ? 'No equipment found matching your search.' : 'No equipment found.'}
              </p>
            ) : (
              paginatedEquipment.map(eq => (
                <MobileCard key={eq.id}>
                  <MobileCardHeader>
                    <MobileCardTitle>{eq.name}</MobileCardTitle>
                    <MobileActions>
                      <ActionButton onClick={() => handleEditEquipment(eq)}>
                        <Edit size={16} />
                      </ActionButton>
                      <ActionButton 
                        className="danger"
                        onClick={() => handleDeleteEquipment(eq)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </MobileActions>
                  </MobileCardHeader>
                </MobileCard>
              ))
            )}
          </div>
        ) : (
          // Desktop table view
          <Table>
            <thead>
              <tr>
                <TableHeader>Equipment Name</TableHeader>
                {/* <TableHeader>Actions</TableHeader> */}
                <TableHeader></TableHeader>
              </tr>
            </thead>
            <tbody>
              {paginatedEquipment.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                    {search ? 'No equipment found matching your search.' : 'No equipment found.'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEquipment.map(eq => (
                  <TableRow key={eq.id}>
                    <TableCell>
                      {eq.name}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <ActionButton onClick={() => handleEditEquipment(eq)}>
                          <Edit size={16} />
                          Edit
                        </ActionButton>
                        <ActionButton 
                          className="danger"
                          onClick={() => handleDeleteEquipment(eq)}
                        >
                          <Trash2 size={16} />
                          Delete
                        </ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredEquipment.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        )}

        {/* Add/Edit Modal */}
        {showModal && createPortal(
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  {modalMode === 'add' ? 'Add New Equipment' : 'Edit Equipment'}
                </ModalTitle>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Equipment Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter equipment name"
                    required
                  />
                </FormGroup>
                <ButtonGroup>
                  <DefaultButton type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </DefaultButton>
                  <PrimaryButton type="submit" disabled={submitting || !formData.name.trim()}>
                    {submitting ? 'Saving...' : modalMode === 'add' ? 'Add Equipment' : 'Update Equipment'}
                  </PrimaryButton>
                </ButtonGroup>
              </form>
            </ModalContent>
          </Modal>,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && createPortal(
          <Modal onClick={() => setShowDeleteConfirm(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Confirm Delete</ModalTitle>
                <CloseButton onClick={() => setShowDeleteConfirm(false)}>×</CloseButton>
              </ModalHeader>
              <p>
                Are you sure you want to delete <strong>{equipmentToDelete?.name}</strong>?
                This action cannot be undone.
              </p>
              <ButtonGroup>
                <DefaultButton onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </DefaultButton>
                <PrimaryButton 
                  onClick={confirmDelete}
                  style={{ backgroundColor: '#d44', borderColor: '#d44' }}
                >
                  Delete
                </PrimaryButton>
              </ButtonGroup>
            </ModalContent>
          </Modal>,
          document.body
        )}
      </Card>
    </PageContainer>
  );
};

export default EquipmentPage; 